/**
 * ManualPropertyCard — single property draft card.
 *
 * Block M1a-FE (TZ 2026-07-29 §9.3).
 * Layout (3-4 grid в MethodBlock):
 *   - Property name (required)
 *   - Unit (optional, narrow)
 *   - Add-values button (popover with textarea, comma/newline splitter)
 *   - Trash icon
 *   - i-tooltip
 *
 * Popover state ЛОКАЛЬНЫЙ — не светит черновик в store до Apply.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Trash2 } from 'lucide-react';

import type { ManualPropertyDraft } from '@/store/matchingStore';
import { cn } from '@/lib/utils';

export interface ManualPropertyCardProps {
  value: ManualPropertyDraft;
  index: number;
  onChange: (
    patch: Partial<Omit<ManualPropertyDraft, 'id'>>,
  ) => void;
  onRemove: () => void;
}

const POPOVER_PLACEHOLDER = 'Cotton\nPolyester\nLinen';

export function ManualPropertyCard({
  value,
  index,
  onChange,
  onRemove,
}: ManualPropertyCardProps) {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draftText, setDraftText] = useState<string>(
    (value.allowedValues ?? []).join('\n'),
  );
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!popoverOpen) return;
    const onDown = (e: PointerEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [popoverOpen]);

  // Sync draftText when opening, если value ещё пустой
  useEffect(() => {
    if (popoverOpen && !draftText) {
      setDraftText((value.allowedValues ?? []).join('\n'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popoverOpen]);

  function handleApplyValues() {
    const tokens = draftText
      .split(/[,\n]/g)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const unique = Array.from(new Set(tokens)).slice(0, 64);
    onChange({ allowedValues: unique.length > 0 ? unique : null });
    setPopoverOpen(false);
  }

  function handleClearValues() {
    onChange({ allowedValues: null });
    setDraftText('');
    setPopoverOpen(false);
  }

  const showNameError = value.name.trim().length === 0;
  const appliedCount = value.allowedValues?.length ?? 0;
  const isByRule = value.mode === 'by_rule';

  return (
    <div
      className={cn(
        'card relative space-y-2 p-3 transition-colors',
        showNameError && 'ring-1 ring-danger-tint/40',
      )}
      data-testid={`manual-property-${index}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-surface-500">
          #{index + 1}
        </span>
        <div className="flex items-center gap-1">
          <span
            title={t('process.matching.composition.manual.infoTooltip', {
              defaultValue:
                'Manual property — used only when the template is missing or for extra fields that the template doesn’t cover.',
            })}
            className="text-surface-500 hover:text-surface-300"
            aria-label="info"
          >
            <Info className="w-3.5 h-3.5" />
          </span>
          <button
            type="button"
            onClick={onRemove}
            title={t('process.matching.composition.manual.delete', {
              defaultValue: 'Remove property',
            })}
            className="text-surface-500 hover:text-danger transition-colors"
            aria-label="delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <input
        type="text"
        value={value.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder={t('process.matching.composition.manual.namePlaceholder', {
          defaultValue: 'e.g. Material',
        })}
        className={cn(
          'input py-1 text-sm w-full',
          showNameError && 'border-danger-tint/50',
        )}
        aria-label={t('process.matching.composition.manual.name', {
          defaultValue: 'Property name',
        })}
      />

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value.unit ?? ''}
          onChange={(e) =>
            onChange({ unit: e.target.value.trim() ? e.target.value.trim() : null })
          }
          placeholder={t('process.matching.composition.manual.unitPlaceholder', {
            defaultValue: 'optional',
          })}
          className="input py-1 text-sm w-24"
          aria-label={t('process.matching.composition.manual.unit', {
            defaultValue: 'Unit',
          })}
        />

        {/* §D.4 mode toggle: only_mine / by_rule */}
        <div
          role="tablist"
          aria-label={t('process.matching.manual.mode', {
            defaultValue: 'Fill mode',
          })}
          className="flex rounded-md border border-surface-700 overflow-hidden shrink-0"
        >
          <button
            type="button"
            role="tab"
            aria-selected={!isByRule}
            onClick={() => onChange({ mode: 'only_mine' })}
            title={t('process.matching.manual.modeOnlyMine', {
              defaultValue:
                'Only mine — value only from the allowed list (or extracted as-is).',
            })}
            className={cn(
              'px-2 py-0.5 text-[11px] transition-colors whitespace-nowrap',
              !isByRule
                ? 'bg-primary-500/20 text-primary-200'
                : 'text-surface-400 hover:text-surface-200',
            )}
          >
            {t('process.matching.manual.modeOnlyMine', {
              defaultValue: 'Only mine',
            })}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isByRule}
            onClick={() => onChange({ mode: 'by_rule' })}
            title={t('process.matching.manual.modeByRule', {
              defaultValue:
                'By rule — value shaped by a deterministic formatting rule; ambiguity stays disputed.',
            })}
            className={cn(
              'px-2 py-0.5 text-[11px] transition-colors whitespace-nowrap',
              isByRule
                ? 'bg-primary-500/20 text-primary-200'
                : 'text-surface-400 hover:text-surface-200',
            )}
          >
            {t('process.matching.manual.modeByRule', {
              defaultValue: 'By rule',
            })}
          </button>
        </div>

        {isByRule && (
          <input
            type="text"
            value={value.formatRule ?? ''}
            onChange={(e) =>
              onChange({
                formatRule: e.target.value.trim() ? e.target.value.trim() : null,
              })
            }
            placeholder={t('process.matching.manual.formatRulePlaceholder', {
              defaultValue:
                'rule: number + unit / lowercase first / as is',
            })}
            className="input py-1 text-sm flex-1"
            aria-label={t('process.matching.manual.formatRule', {
              defaultValue: 'Formatting rule',
            })}
          />
        )}
        {!isByRule && (
        <div className="relative flex-1" ref={popoverRef}>
          <button
            type="button"
            onClick={() => setPopoverOpen((v) => !v)}
            className={cn(
              'w-full flex items-center justify-between gap-1 px-2 py-1 rounded-md border text-xs transition-colors',
              appliedCount > 0
                ? 'border-primary-500/40 bg-primary-500/10 text-primary-200'
                : 'border-surface-700 bg-surface-900 text-surface-300 hover:border-surface-500',
            )}
            aria-haspopup="dialog"
            aria-expanded={popoverOpen}
          >
            <span>
              {appliedCount > 0
                ? t('process.matching.composition.manual.valuesApplied', {
                    defaultValue: 'Applied: {n}',
                    n: appliedCount,
                  })
                : t('process.matching.composition.manual.addValues', {
                    defaultValue: 'Add values',
                  })}
            </span>
          </button>

          {popoverOpen && (
            <div
              role="dialog"
              aria-label={t('process.matching.composition.manual.popoverTitle', {
                defaultValue: 'Allowed values',
              })}
              className="absolute z-30 left-0 right-0 top-full mt-1 card shadow-xl p-3 space-y-2"
            >
              <div className="text-xs font-semibold text-surface-200">
                {t('process.matching.composition.manual.popoverTitle', {
                  defaultValue: 'Allowed values',
                })}
              </div>
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder={POPOVER_PLACEHOLDER}
                rows={5}
                className="input py-1.5 text-sm w-full font-mono"
              />
              <p className="text-[11px] text-surface-500 leading-snug">
                {t('process.matching.composition.manual.popoverHint', {
                  defaultValue:
                    'Separate by comma or line break. Each line becomes one allowed value.',
                })}
              </p>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleClearValues}
                  className="btn-secondary text-xs px-2 py-1"
                  disabled={appliedCount === 0}
                >
                  {t('process.matching.composition.manual.valuesEmpty', {
                    defaultValue: 'No allowed values',
                  })}
                </button>
                <button
                  type="button"
                  onClick={handleApplyValues}
                  className="btn-primary text-xs px-3 py-1"
                >
                  {t('process.matching.composition.manual.allowedValuesLabel', {
                    defaultValue: 'Allowed values',
                  })}
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
